import type { Href } from "expo-router";
import { router, useLocalSearchParams } from "expo-router";
import { useRef, useState } from "react";
import { ScrollView, Text, View } from "react-native";

import { COLORS, FONTS } from "../../../../../../lib/brand";
import { PANE_THEMES } from "../../../../../../components/brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;

import {
  StudentProgramShell,
  type ProgramTab,
} from "../../../../../../components/student/StudentProgramShell";
import { useStudentPortalShellData } from "../../../../../../lib/useStudentPortalShellData";
import { Button, Card, EmptyState, Input, Pill } from "../../../../../../components/ui";
import { createTeam } from "../../../../../../lib/teams";

/**
 * Inline create-team form. Mounted under `/student/competition/[id]/teams/new`,
 * shares the v2 program shell (persistent header + green program sidebar) so
 * the chrome matches the rest of the workspace. The sidebar's Team tab is the
 * active one; clicking another tab routes back to the program workspace.
 */
export default function NewTeamScreen() {
  const params = useLocalSearchParams<{ id: string }>();
  const competitionId = typeof params.id === "string" ? params.id : "";
  const shell = useStudentPortalShellData();

  const [name, setName] = useState("");
  // Ref updated synchronously on toggle so submit can not race ahead of a pending setState.
  const visibilityRef = useRef<"public" | "private">("public");
  const [visibility, setVisibility] = useState<"public" | "private">("public");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function selectVisibility(next: "public" | "private") {
    visibilityRef.current = next;
    setVisibility(next);
  }

  function goBackOrToCompetition() {
    if (router.canGoBack()) router.back();
    else if (competitionId) {
      router.replace(`/student/competition/${competitionId}` as Href);
    } else {
      router.replace("/student" as Href);
    }
  }

  async function onSubmit() {
    if (!competitionId) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Team name is required.");
      return;
    }
    setSaving(true);
    setError(null);
    const chosenVisibility = visibilityRef.current;
    const { error: err } = await createTeam({
      competitionId,
      name: trimmed,
      visibility: chosenVisibility,
    });
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    goBackOrToCompetition();
  }

  function handleTabChange(next: ProgramTab) {
    if (!competitionId) {
      router.replace("/student" as Href);
      return;
    }
    if (next === "team") return;
    router.replace(`/student/competition/${competitionId}?tab=${next}` as Href);
  }

  if (!competitionId) {
    return (
      <StudentProgramShell
        topbarTitle="Create a team"
        topbarFirstName={shell.topbarFirstName}
        avatarName={shell.avatarName}
        avatarUrl={shell.avatarUrl}
        identity={shell.identity}
        casesBadgeCount={shell.casesBadgeCount}
        activeTab={"team" as ProgramTab}
        hasJoinedTeam={false}
        competitionId={null}
        onTabChange={() => router.replace("/student" as Href)}
      >
        <View className="flex-1 items-center justify-center px-4 py-16">
          <EmptyState title="Missing competition" subtitle="Return to the student dashboard and try again." />
        </View>
      </StudentProgramShell>
    );
  }

  return (
    <StudentProgramShell
      topbarTitle="Create a team"
      topbarFirstName={shell.topbarFirstName}
      avatarName={shell.avatarName}
      avatarUrl={shell.avatarUrl}
      identity={shell.identity}
      casesBadgeCount={shell.casesBadgeCount}
      activeTab={"team" as ProgramTab}
      hasJoinedTeam={false}
      competitionId={competitionId}
      onTabChange={handleTabChange}
    >
      <ScrollView
        className="flex-1 bg-sand"
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ paddingBottom: 40 }}
      >
        <View className="px-5 pt-8 pb-2 md:px-11">
          <Text
            className="text-[10px] font-bold uppercase"
            style={{ fontFamily: FONTS.bodyBold, color: COLORS.AMBER.hex, letterSpacing: 2 }}
          >
            New team
          </Text>
          <Text
            className="mt-1 text-[34px] leading-tight"
            style={{ fontFamily: FONTS.display, color: COLORS.FOREST.hex }}
          >
            Set up your team
          </Text>
          <Text
            className="mt-2 text-[13px] leading-[19px]"
            style={{ fontFamily: FONTS.body, color: STUDENT_THEME.textOnSurfaceDim }}
          >
            Pick a team name and visibility before you dive into deliverables.
          </Text>
        </View>

        <View className="px-5 py-6 md:px-11">
          <Card variant="light" padding="lg">
            {error ? (
              <Text className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {error}
              </Text>
            ) : null}

            <Input label="Team name" value={name} onChangeText={setName} placeholder="e.g. Solo Run" />

            <Text className="mt-6 text-sm font-medium text-text" style={{ fontFamily: FONTS.bodySemiBold }}>
              Visibility
            </Text>
            <Text className="mt-1 text-xs text-text/55" style={{ fontFamily: FONTS.body }}>
              Public teams can be joined in one click. Private teams require a captain-approved request.
            </Text>
            <View className="mt-2 flex-row gap-2">
              <Pill selected={visibility === "public"} onPress={() => selectVisibility("public")}>
                Public
              </Pill>
              <Pill selected={visibility === "private"} onPress={() => selectVisibility("private")}>
                Private
              </Pill>
            </View>

            <View className="mt-8 gap-3">
              <Button onPress={() => void onSubmit()} loading={saving}>
                Create team
              </Button>
              <Button variant="ghost-light" onPress={goBackOrToCompetition} disabled={saving}>
                Cancel
              </Button>
            </View>
          </Card>
        </View>
      </ScrollView>
    </StudentProgramShell>
  );
}
